<template>
  <div>
    <Modal
      :show="showModal"
      size="full"
      :show-close-button="false"
      :close-on-backdrop="!isPostCallLocked"
      :close-on-escape="false"
      :blur-backdrop="true"
      @close="closeModal()"
    >
      <template #body>
        <div class="workspace-layout">
          <!-- LEFT: Templates -->
          <div v-if="selectedBucket && is3Up" class="workspace-panel workspace-panel--left">
            <TemplateViewerModal @close="closeModal()" />
          </div>

          <!-- CENTER: Interaction -->
          <div v-if="selectedBucket" class="workspace-panel workspace-panel--center">
            <div class="interaction-panel">
              <CallInteractionHeader
                v-model:activeTab="activeInteractionTab"
                :contact="reactiveContact"
                :tabs="tabsWithBadges"
                @close="closeModal()"
              />

              <!-- Upcoming meeting banner -->
              <div v-if="upcomingMeetings.length > 0" class="upcoming-meeting-wrapper">
                <div
                  class="upcoming-meeting-banner"
                  role="button"
                  tabindex="0"
                  :aria-expanded="isUpcomingMeetingsExpanded"
                  @click="toggleUpcomingMeetingsList"
                  @keydown.enter="toggleUpcomingMeetingsList"
                >
                  <div class="upcoming-meeting-banner__icon-wrap">
                    <Icon icon="mdi:calendar-check" width="15" height="15" />
                  </div>
                  <div class="upcoming-meeting-banner__body">
                    <span class="upcoming-meeting-banner__title">
                      {{
                        upcomingMeetings.length === 1
                          ? "Meeting Scheduled"
                          : `${upcomingMeetings.length} Meetings Scheduled`
                      }}
                    </span>
                    <span class="upcoming-meeting-banner__detail">
                      {{ formatUpcomingMeeting(upcomingMeetings[0].meetingDateTime) }}
                      <span
                        v-if="upcomingMeetings.length > 1"
                        v-tooltip="{ content: upcomingMeetingsTooltip, placement: 'bottom' }"
                        class="upcoming-meeting-banner__more"
                        @click.stop
                      >
                        <Icon icon="mdi:information-outline" width="11" height="11" />
                        +{{ upcomingMeetings.length - 1 }} more
                      </span>
                    </span>
                  </div>
                  <Icon
                    icon="mdi:chevron-right"
                    class="upcoming-meeting-banner__chevron"
                    :class="{
                      'upcoming-meeting-banner__chevron--expanded': isUpcomingMeetingsExpanded,
                    }"
                    width="16"
                    height="16"
                  />
                </div>
                <div v-if="isUpcomingMeetingsExpanded" class="upcoming-meetings-list">
                  <div
                    v-for="meeting in upcomingMeetings"
                    :key="meeting.id"
                    class="upcoming-meetings-list__item"
                  >
                    <Icon
                      icon="mdi:calendar-clock-outline"
                      class="upcoming-meetings-list__item-icon"
                    />
                    <div class="upcoming-meetings-list__item-text">
                      <span class="upcoming-meetings-list__item-title">{{
                        meeting.title || "Meeting"
                      }}</span>
                      <span class="upcoming-meetings-list__item-date">{{
                        formatUpcomingMeeting(meeting.meetingDateTime)
                      }}</span>
                    </div>
                    <button
                      type="button"
                      class="upcoming-meetings-list__item-action"
                      @click="openUpcomingMeetingDetails(meeting)"
                    >
                      See Details
                    </button>
                  </div>
                </div>
              </div>

              <!-- Converted-to-opportunity banner, colored by the opportunity's stage -->
              <div
                v-if="linkedOpportunity"
                class="opportunity-banner"
                :style="{
                  background: opportunityBannerColors.background,
                  borderBottomColor: opportunityBannerColors.border,
                }"
              >
                <div
                  class="opportunity-banner__icon-wrap"
                  :style="{
                    background: opportunityBannerColors.iconBackground,
                    color: opportunityBannerColors.text,
                  }"
                >
                  <Icon icon="mdi:briefcase-check-outline" width="15" height="15" />
                </div>
                <div class="opportunity-banner__body">
                  <span
                    class="opportunity-banner__title"
                    :style="{ color: opportunityBannerColors.text }"
                  >
                    Opportunity
                  </span>
                  <span
                    v-if="linkedOpportunityStage?.name"
                    class="opportunity-banner__stage-pill"
                    :style="stagePillStyle"
                  >
                    {{ linkedOpportunityStage.name }}
                  </span>
                  <span
                    class="opportunity-banner__detail"
                    :style="{ color: opportunityBannerColors.text }"
                  >
                    {{ linkedOpportunity.name }}
                  </span>
                </div>
                <button
                  type="button"
                  class="opportunity-banner__view"
                  :style="{ color: opportunityBannerColors.text }"
                  @click="goToLinkedOpportunity"
                >
                  View
                  <Icon icon="mdi:arrow-top-right" width="12" height="12" />
                </button>
              </div>

              <div class="interaction-panels-container">
                <CallInteractionPanel
                  v-show="activeInteractionTab === 'calls'"
                  :is-active="activeInteractionTab === 'calls'"
                  :selectedBucket="selectedBucket"
                  :contact="reactiveContact"
                  :incomingCallData="incomingCallData"
                  :icps="icps"
                  :icp-loading="icpsLoading"
                  :pendingCueCardContent="pendingCueCardContent"
                  @refresh-buckets="$emit('refreshBuckets')"
                  @save-and-log-call="handleSaveAndLogCall"
                  @close-modal="closeModal()"
                  @show-edit-contact-modal="showEditContactModal = true"
                  @decline-incoming-call="handleDeclineIncomingCall"
                  @open-ai-personalization="handleOpenAiPersonalization"
                  @switch-channel="handleSwitchChannelWithContent"
                  @content-applied="pendingCueCardContent = null"
                />
                <WhatsAppInteractionPanel
                  v-show="activeInteractionTab === 'whatsapp'"
                  :is-active="activeInteractionTab === 'whatsapp'"
                  :selectedBucket="selectedBucket"
                  :contact="reactiveContact"
                  :whatsAppChannel="whatsAppChannel"
                  :icps="icps"
                  :icp-loading="icpsLoading"
                  :is-channels-loading="isChannelsLoading"
                  :initialConversationId="effectiveWhatsAppChatId"
                  :initial-message-id="historyWhatsAppMessageId"
                  @refresh-conversations="loadConversationsForChannel('whatsapp')"
                  @message-sent="loadConversationsForChannel('whatsapp')"
                  @show-edit-contact-modal="showEditContactModal = true"
                  @reconnect-requested="handleReconnectWhatsApp"
                  @open-ai-personalization="handleOpenAiPersonalization"
                  @switch-channel="handleSwitchChannelWithContent"
                  :pendingCueCardContent="pendingCueCardContent"
                  @content-applied="pendingCueCardContent = null"
                />
                <LinkedInInteractionPanel
                  v-show="activeInteractionTab === 'linkedin'"
                  :is-active="activeInteractionTab === 'linkedin'"
                  :selectedBucket="selectedBucket"
                  :linkedInChannel="linkedInChannel"
                  :icps="icps"
                  :icp-loading="icpsLoading"
                  :is-channels-loading="isChannelsLoading"
                  :initialConversationId="effectiveLinkedInChatId"
                  :initial-message-id="historyLinkedInMessageId"
                  @refresh-conversations="loadConversationsForChannel('linkedin')"
                  @message-sent="loadConversationsForChannel('linkedin')"
                  @show-edit-contact-modal="showEditContactModal = true"
                  @reconnect-requested="handleReconnectLinkedIn"
                  @open-ai-personalization="handleOpenAiPersonalization"
                  @switch-channel="handleSwitchChannelWithContent"
                  :pendingCueCardContent="pendingCueCardContent"
                  @content-applied="pendingCueCardContent = null"
                />
                <EmailInteractionPanel
                  v-show="activeInteractionTab === 'email'"
                  :is-active="activeInteractionTab === 'email'"
                  :selectedBucket="selectedBucket"
                  :emailChannel="emailChannel"
                  :emailAccounts="emailAccounts"
                  :icps="icps"
                  :icp-loading="icpsLoading"
                  :is-channels-loading="isChannelsLoading"
                  :initial-thread-id="effectiveEmailThreadId"
                  @refresh-conversations="loadConversationsForChannel('email')"
                  @email-sent="loadConversationsForChannel('email')"
                  @connect-account="handleConnectEmailAccount"
                  @open-ai-personalization="handleOpenAiPersonalization"
                  @switch-channel="handleSwitchChannelWithContent"
                  :pendingCueCardContent="pendingCueCardContent"
                  @content-applied="pendingCueCardContent = null"
                />
                <SMSInteractionPanel
                  v-show="activeInteractionTab === 'sms'"
                  :is-active="activeInteractionTab === 'sms'"
                  :selectedBucket="selectedBucket"
                  :contact="reactiveContact"
                  :icps="icps"
                  :icp-loading="icpsLoading"
                  :is-channels-loading="isChannelsLoading"
                  @refresh-conversations="loadConversationsForChannel('sms')"
                  @message-sent="loadConversationsForChannel('sms')"
                  @show-edit-contact-modal="showEditContactModal = true"
                  @open-ai-personalization="handleOpenAiPersonalization"
                  @switch-channel="handleSwitchChannelWithContent"
                  :pendingCueCardContent="pendingCueCardContent"
                  @content-applied="pendingCueCardContent = null"
                />
                <ChatInteractionPanel
                  v-show="activeInteractionTab === 'chat'"
                  :is-active="activeInteractionTab === 'chat'"
                  :selectedBucket="selectedBucket"
                />
              </div>
            </div>
          </div>

          <!-- RIGHT: Task Follow-up -->
          <div v-if="selectedBucket" class="workspace-panel workspace-panel--right">
            <NextTaskFollowupModal
              :key="nextTaskKey"
              :selectedBucket="selectedBucket"
              :contact="reactiveContact"
              :bucketIsLoading="bucketIsLoading"
              :bucketOptions="bucketOptions"
              @close="closeModal()"
              @save="handleNextTaskSave"
              @show-notes-modal="showNotesModal = true"
              @show-last-interaction-modal="showLastInteractionModal = true"
              @show-edit-contact-modal="showEditContactModal = true"
              @show-book-meeting-modal="showBookMeetingModal = true"
              @show-linkedin-profile-modal="showLinkedInProfileModal = true"
              @show-company-profile-modal="showCompanyProfileModal = true"
            />
          </div>
        </div>
      </template>
    </Modal>

    <button
      v-if="showModal && selectedBucket && !is3Up"
      type="button"
      class="templates-fab"
      :class="{ active: isTemplatesOpen }"
      :aria-expanded="isTemplatesOpen"
      :aria-pressed="isTemplatesOpen"
      :title="isTemplatesOpen ? 'Close templates (])' : 'Open templates (])'"
      @click="toggleTemplatesModal"
    >
      <Icon icon="mdi:file-document-outline" width="15" height="15" />
      <span>Templates</span>
    </button>

    <TemplatesFloatingModal
      v-if="!is3Up"
      :show="isTemplatesOpen && Boolean(selectedBucket)"
      @close="closeTemplatesModal"
    />

    <!-- Child modals — outside workspace for proper viewport centering -->
    <NotesModal
      v-if="showNotesModal == true"
      :selectedContact="reactiveContact"
      @close-notes-modal="showNotesModal = false"
    />
    <BookMeetingModal
      v-if="showBookMeetingModal == true"
      :selectedBucket="selectedBucket"
      @close-book-meeting-modal="showBookMeetingModal = false"
      @show-reminder-followup-modal="
        (meeting: any) => {
          openReminderFollowupModal(meeting);
          showReminderFollowupModal = true;
        }
      "
    />
    <ReminderFollowupModal
      v-if="showReminderFollowupModal == true"
      :selectedMeeting="selectedMeeting"
      @close-reminder-followup-modal="
        showReminderFollowupModal = false;
        closeReminderFollowupModal();
      "
      @close-parent-modal="closeParentModal"
    />
    <MeetingDetailsModal
      v-if="showUpcomingMeetingModal"
      :selectedMeeting="upcomingMeetingForModal"
      @close-meeting-details-modal="showUpcomingMeetingModal = false"
      @refresh-meetings="refetchUpcomingMeetings"
    />
    <EditContactModal
      v-if="showEditContactModal == true"
      :selectedContact="reactiveContact"
      @close-edit-contact-modal="showEditContactModal = false"
      @refresh-contacts="getContact()"
    />
    <LastInteractionModal
      v-if="showLastInteractionModal == true"
      ref="lastInteractionModal"
      :contactId="reactiveContact?.id || selectedBucket?.contactId"
      :contact-email="reactiveContact?.email || ''"
      :key="reactiveContact?.id ?? selectedBucket?.contactId ?? 'loading'"
      @close-last-interaction-modal="showLastInteractionModal = false"
      @navigate-to-tab="handleNavigateFromInteractionHistory"
    />
    <DeleteConfirmationModal
      v-if="showDeleteConfirmationModal == true"
      :deleteFunction="deleteContactPhoneNumber"
      entity="Phone Number"
      @close-delete-confirmation-modal="showDeleteConfirmationModal = false"
      @refresh-records="getContact()"
    />
    <ForwardMessageModal
      :show="showForwardModal"
      :conversations="forwardableConversations"
      :channel-type="activeInteractionTab"
      :is-loading="isLoadingConversations"
      :is-loading-more="false"
      :has-more="false"
      @close="showForwardModal = false"
      @forward="handleForwardConfirm"
      @search="handleForwardSearch"
      @load-more="handleForwardLoadMore"
    />
    <DispositionReminderModal
      v-if="showCloseConfirmationModal"
      :isLoading="bucketIsLoading || isLoggingDetails"
      :selectedCallDisposition="selectedCallDisposition"
      @cancel="handleCancel"
    />
    <EmailCompositionModal
      v-if="showEmailCompositionModal"
      :show="showEmailCompositionModal"
      :recipient-email="reactiveContact?.email"
      :accounts="emailAccounts"
      :default-account-id="emailChannel?.accountId"
      :contact-id="reactiveContact?.id || selectedBucket?.contactId || ''"
      @close="showEmailCompositionModal = false"
      @send="handleEmailSend"
      @attach="handleEmailAttach"
      @schedule-send="handleEmailScheduleSend"
    />
    <LinkedInConnectionRequestModal
      :show="showLinkedInConnectionModal"
      :contact-name="contactFullName"
      :is-loading="isSendingLinkedInInvite"
      :error="linkedInInviteError"
      @close="handleLinkedInConnectionModalClose"
      @send="handleLinkedInConnectionSend"
    />
    <AIPersonalizationModal
      :show-modal="showAiPersonalizationModal"
      :contact-name="aiPersonalizationContext.contactName"
      :icp-name="aiPersonalizationContext.icpName"
      :default-type="aiPersonalizationContext.defaultType as any"
      :contact-id="aiPersonalizationContext.contactId"
      :icp-id="aiPersonalizationContext.icpId"
      @close="showAiPersonalizationModal = false"
    />
    <StartWhatsAppChatModal
      :show="showStartChatModal"
      :channels="availableChannelsForChat as any"
      :default-account-id="getChannelForType(activeInteractionTab)?.accountId"
      :is-loading="isCreatingChat"
      :auto-set-channel-type="
        activeInteractionTab === 'whatsapp' || activeInteractionTab === 'linkedin'
          ? activeInteractionTab
          : undefined
      "
      :hide-account-selection="true"
      :contact-phone-numbers="activeInteractionTab === 'whatsapp' ? contactPhoneNumbers : undefined"
      :contact-id="reactiveContact?.id || selectedBucket?.contactId"
      :icp-id="icps?.find((i) => i.isDefault)?.id"
      @close="showStartChatModal = false"
      @create-chat="handleCreateChat"
    />
    <ConnectChannelModal
      :show="showConnectChannelModal"
      :channel="channelToConnect"
      :is-loading="isConnectingChannel"
      @close="showConnectChannelModal = false"
      @connect="handleConnectChannel"
    />
    <LinkedInProfileViewerModal
      :show="showLinkedInProfileModal"
      :selectedBucket="selectedBucket"
      :linkedInChannel="linkedInChannel"
      @close="showLinkedInProfileModal = false"
    />
    <CompanyProfileViewerModal
      :show="showCompanyProfileModal"
      :selectedBucket="selectedBucket"
      :linkedInChannel="linkedInChannel"
      @close="showCompanyProfileModal = false"
    />
    <div v-if="isPostCallLocked && !isDispositionPending" class="post-call-loading-overlay">
      <div class="post-call-loading-card">
        <div class="post-call-loading-spinner"></div>
        <p class="post-call-loading-text">Preparing call summary...</p>
      </div>
    </div>
    <CallDispositionRequiredModal
      v-if="isDispositionPending && pendingCallSid"
      :call-sid="pendingCallSid"
      @saved="onDispositionSaved"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted, useTemplateRef } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { Icon } from "@iconify/vue";

import DeleteConfirmationModal from "@/components/modal/DeleteConfirmationModal.vue";
import CallInteractionHeader from "@/components/bucket/interactions/CallInteractionHeader.vue";
import CallInteractionPanel from "@/components/bucket/interactions/CallInteractionPanel.vue";
import EmailInteractionPanel from "@/components/bucket/interactions/EmailInteractionPanel.vue";
import WhatsAppInteractionPanel from "@/components/bucket/interactions/WhatsAppInteractionPanel.vue";
import LinkedInInteractionPanel from "@/components/bucket/interactions/LinkedInInteractionPanel.vue";
import DispositionReminderModal from "@/components/bucket/DispositionReminderModal.vue";
import EmailCompositionModal from "@/components/bucket/email/EmailCompositionModal.vue";
import LastInteractionModal from "@/components/bucket/LastInteractionModal.vue";
import LinkedInConnectionRequestModal from "@/components/bucket/linkedin/LinkedInConnectionRequestModal.vue";
import EditContactModal from "@/components/contacts/EditContactModal.vue";
import BookMeetingModal from "@/components/meeting/BookMeetingModal.vue";
import ReminderFollowupModal from "@/components/meeting/ReminderFollowupModal.vue";
import MeetingDetailsModal from "@/components/meeting/MeetingDetailsModal.vue";
import { useMeetings } from "@/composables/meetings/useMeetings";
import { useOpportunityByContact, useOpportunityStages } from "@/composables/opportunities";
import { OPPORTUNITY_OVERVIEW_ROUTE } from "@/router/routes";
import NotesModal from "@/components/note/NotesModal.vue";
import ForwardMessageModal from "@/components/socials/ForwardMessageModal.vue";
import StartWhatsAppChatModal from "@/components/socials/StartWhatsAppChatModal.vue";
import ConnectChannelModal from "@/components/socials/ConnectChannelModal.vue";
import TemplateViewerModal from "@/components/template/TemplateViewerModal.vue";
import TemplatesFloatingModal from "@/components/template/TemplatesFloatingModal.vue";
import Modal from "@/components/ui/Modal.vue";
import AIPersonalizationModal from "@/components/template/AIPersonalizationModal.vue";
import NextTaskFollowupModal from "@/components/bucket/NextTaskFollowupModal.vue";
import LinkedInProfileViewerModal from "@/components/bucket/linkedin/LinkedInProfileViewerModal.vue";
import CompanyProfileViewerModal from "@/components/bucket/company/CompanyProfileViewerModal.vue";
import SMSInteractionPanel from "@/components/bucket/interactions/SMSInteractionPanel.vue";
import ChatInteractionPanel from "@/components/bucket/interactions/ChatInteractionPanel.vue";

import { defineRule } from "vee-validate";

import { useMessageWebhooks } from "@/composables/messages/useMessageWebhooks";
import { useEmailWebhooks } from "@/composables/emails/useEmailWebhooks";

import { useToast } from "@/composables/ui/useToast";
import {
  useResponsiveBreakpoint,
  WORKSPACE_3UP_MIN_PX,
} from "@/composables/ui/useResponsiveBreakpoint";
import { usePersistentToggle } from "@/composables/ui/usePersistentToggle";
import { useICPs } from "@/composables/icp/useICPs";
import { disableBeforeUnload } from "@/utils/unloadGuard";
import { useDeleteContactPhoneNumber } from "@/composables/contacts";
import { useTaskFollowupContact } from "@/composables/bucket/useTaskFollowupContact";
import { usePostCallLock } from "@/composables/calls/usePostCallLock";
import { useCallLogBySid } from "@/composables/calls/useCallLogBySid";
import CallDispositionRequiredModal from "@/components/call-disposition/CallDispositionRequiredModal.vue";

// TanStack Query composables
import { useBucketOptions } from "@/composables/bucket/useBucketOptions";
import { useCallDispositions } from "@/composables/calls/useCallDispositions";
import { usePhoneNumbers } from "@/composables/calls/usePhoneNumbers";
import { useUpdateBucket, useRegisterBucket } from "@/composables/bucket/useBucket";
import { useLogCallLogDetails } from "@/composables/calls/useCallLog";
import { useRegisterActivityLog } from "@/composables/activityLogs/useRegisterActivityLog";

// Task-followup domain composables
import {
  useCallDispositionState,
  useChannelManager,
  useLinkedInActions,
  useEmailActions,
  useWhatsAppActions,
  useInteractionTabs,
  useSocketListeners,
  usePhoneSelection,
  useBucketForm,
} from "@/composables/task-followup";

// Define validation rules
defineRule("email", (value: any) => {
  const emailRegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegExp.test(value) || "Email is not valid";
});

defineRule("falseyEmail", (value: any) => {
  if (!value) {
    return true;
  }
  const emailRegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegExp.test(value) || "Email is not valid";
});

defineRule("required", (value: any, [fieldName = "Field"]: any) => {
  if (!value || value.length === 0) {
    return `${fieldName} is required`;
  }
  return true;
});

defineRule("alpha", (value: any, [fieldName = "Field"]: any) => {
  const alphaRegExp = /^[a-zA-Z]*$/;
  return alphaRegExp.test(value) || `${fieldName} must contain only alphabetic characters`;
});

// Props
const props = defineProps<{
  selectedBucket?: Record<string, any>;
  incomingCallData?: Record<string, any> | null;
  initialEmailThreadId?: string | null;
  initialChatId?: string | null;
}>();

// Emits
const emit = defineEmits<{
  refreshBuckets: [];
  closeTaskFollowupModal: [];
  showMoveBucketModal: [contact: any];
}>();

// Globals
const store = useStore();
const router = useRouter();
const toast = useToast();

const account = computed(() => store.getters.account?.data);
const orgId = computed(() => account.value?.organizationId);

// TanStack Query data composables
const bucketOptionsParams = computed(() => (orgId.value ? { organizationId: orgId.value } : null));
const { data: bucketOptions } = useBucketOptions(bucketOptionsParams);

const { data: callDispositions } = useCallDispositions();

const phoneNumbersParams = computed(() => (orgId.value ? { organizationId: orgId.value } : null));
const { data: phoneNumbersResult } = usePhoneNumbers(phoneNumbersParams);
const phoneNumbers = computed(() => phoneNumbersResult.value?.data ?? []);

// ICPs
const { data: icpsData, isLoading: icpsLoading } = useICPs();
const icps = computed(() => icpsData.value ?? []);

// TanStack Query mutations
const { mutateAsync: updateBucket, isPending: isUpdatingBucket } = useUpdateBucket();
const { mutateAsync: registerBucket, isPending: isRegisteringBucket } = useRegisterBucket();
const bucketIsLoading = computed(() => isUpdatingBucket.value || isRegisteringBucket.value);
const { mutateAsync: logCallLogDetailsMutation, isPending: isLoggingDetails } =
  useLogCallLogDetails();
const { mutateAsync: registerActivityLog } = useRegisterActivityLog();

// Existing composables
const selectedBucketRef = computed(() => props.selectedBucket);
const { contact: reactiveContact } = useTaskFollowupContact(selectedBucketRef);
const { deleteContactPhoneNumber: deletePhoneMutation } = useDeleteContactPhoneNumber();

// Upcoming meetings for this contact (future only)
const upcomingMeetingsParams = computed(() => {
  const contactId = reactiveContact.value?.id;
  if (!orgId.value || !contactId) return null;
  return { organizationId: orgId.value, contactId, status: "pending", limit: 5 };
});
const { data: upcomingMeetingsData, refetch: refetchUpcomingMeetings } =
  useMeetings(upcomingMeetingsParams);
const nowRef = ref(new Date());
let _nowInterval: ReturnType<typeof setInterval> | null = null;
const upcomingMeetings = computed(() =>
  (upcomingMeetingsData.value?.data ?? []).filter(
    (m: any) => new Date(m.meetingDateTime) > nowRef.value
  )
);

const showUpcomingMeetingModal = ref(false);
const upcomingMeetingForModal = ref<any>(null);
const isUpcomingMeetingsExpanded = ref(false);

const toggleUpcomingMeetingsList = () => {
  isUpcomingMeetingsExpanded.value = !isUpcomingMeetingsExpanded.value;
};

const openUpcomingMeetingDetails = (meeting: any) => {
  if (!meeting) return;
  upcomingMeetingForModal.value = meeting;
  showUpcomingMeetingModal.value = true;
  isUpcomingMeetingsExpanded.value = false;
};

const formatUpcomingMeeting = (isoString: string) => {
  const d = new Date(isoString);
  return (
    d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    }) +
    " · " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
  );
};

const upcomingMeetingsTooltip = computed(() =>
  upcomingMeetings.value.map((m: any) => formatUpcomingMeeting(m.meetingDateTime)).join("  •  ")
);

// Opportunity this contact was converted to, if any (a contact has at most one)
const opportunityByContactParams = computed(() => {
  const contactId = reactiveContact.value?.id || props.selectedBucket?.contactId;
  if (!orgId.value || !contactId) return null;
  return { organizationId: orgId.value, contactId };
});
const { data: linkedOpportunity } = useOpportunityByContact(opportunityByContactParams);

// Opportunity.getOpportunities only returns a flat opportunityStageId (see
// OpportunityOverview.vue's own "currentStage" lookup), never a nested opportunityStage
// object, so the stage's name/colors have to be looked up separately from the org's
// stage list — same { organizationId } query OpportunityOverview.vue already uses, so
// this reuses that query's cache instead of firing a fresh request in most cases.
const opportunityStagesParams = computed(() => {
  if (!orgId.value) return null;
  return { organizationId: orgId.value };
});
const { data: opportunityStagesData } = useOpportunityStages(opportunityStagesParams);
const linkedOpportunityStage = computed(() => {
  const stages = opportunityStagesData.value;
  const stageId = linkedOpportunity.value?.opportunityStageId;
  if (!Array.isArray(stages) || !stageId) return null;
  return stages.find((stage) => stage.id === stageId) || null;
});

const hexToRgba = (hex: string | undefined | null, alpha: number) => {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#") || hex.length < 7) return null;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Colored entirely from the opportunity's own stage (not a fixed palette), so the
// banner always matches whatever color the org configured for that stage.
const opportunityBannerColors = computed(() => {
  const stage = linkedOpportunityStage.value;
  const text = stage?.textColor || "#4338ca";
  return {
    background: stage?.backgroundColor || hexToRgba(text, 0.08) || "#eef2ff",
    border: hexToRgba(text, 0.35) || "#c7d2fe",
    iconBackground: hexToRgba(text, 0.16) || "#e0e7ff",
    text,
  };
});

// Same pill treatment used for opportunity stages in the contact activity timeline
// (see MovementTimelineItem.vue's pillStyle helper) — tinted background from the
// stage color at ~12% opacity, solid border/text in the stage color itself. Routed
// through hexToRgba (rather than string-concatenating a hex alpha suffix) so it
// doesn't break on 3-digit hex stage colors.
const stagePillStyle = computed(() => {
  const color = linkedOpportunityStage.value?.textColor || "#6b7280";
  return {
    backgroundColor: hexToRgba(color, 0.12) || color + "20",
    color,
    borderColor: color,
  };
});

const goToLinkedOpportunity = () => {
  if (!linkedOpportunity.value?.id) return;
  router.push({ name: OPPORTUNITY_OVERVIEW_ROUTE, params: { id: linkedOpportunity.value.id } });
};

const {
  isPostCallLocked,
  isDispositionPending,
  pendingCallSid,
  onCallSummaryReady,
  onDispositionSaved,
  clearPostCallLock,
} = usePostCallLock();

const { data: pendingCallLog, isLoading: callLogLoading } = useCallLogBySid(pendingCallSid, orgId);

watch([callLogLoading, pendingCallLog], ([loading, callLog]) => {
  if (!pendingCallSid.value) return;
  if (!loading && callLog) onCallSummaryReady();
});

const currentCallSid = ref<string | null>(null);
const parentCallSid = ref<string | null>(null);
const lastCallSid = ref<string | null>(null);
const to = ref<string>("");

const {
  selectedCallDisposition,
  callDispositionError,
  mustLogAfterCall,
  hasCallBeenLogged,
  hasBaseCallLogCreated,
  isCallDispositionRequired,
  canProceedWithCall,
  logCallBySid,
  onCallDispositionChange,
} = useCallDispositionState(
  currentCallSid,
  logCallLogDetailsMutation,
  callDispositions,
  reactiveContact,
  toast
);

const {
  whatsAppChannel,
  linkedInChannel,
  emailChannel,
  emailAccounts,
  isChannelsLoading,
  showConnectChannelModal,
  channelToConnect,
  isConnectingChannel,
  isEmailAccountConnected,
  availableChannelsForChat,
  isChannelHealthyStatus,
  loadChannels,
  getChannelForType,
  handleConnectEmailAccount,
  handleReconnectLinkedIn,
  handleReconnectWhatsApp,
  handleConnectChannel,
} = useChannelManager(toast);

// Interaction tabs first (provides loadConversationsForChannel)
const {
  activeInteractionTab,
  interactionTabs,
  isLoadingConversations,
  tabsWithBadges,
  interactionCounts,
  incrementBadge,
  mapTaskTypeToTab,
  loadConversationsForChannel,
} = useInteractionTabs(props, reactiveContact, () => props.incomingCallData);

const {
  isSendingLinkedInInvite,
  linkedInInviteError,
  showLinkedInConnectionModal,
  showLinkedInProfileModal,
  showCompanyProfileModal,
  canSendLinkedInInvite,
  extractLinkedInIdentifierFromUrl,
  handleLinkedInConnectionModalClose,
  handleLinkedInConnectionSend,
} = useLinkedInActions(
  reactiveContact,
  linkedInChannel,
  isChannelHealthyStatus,
  loadConversationsForChannel,
  toast
);

const { showEmailCompositionModal, handleEmailSend, handleEmailAttach, handleEmailScheduleSend } =
  useEmailActions(loadConversationsForChannel, toast);

const {
  forwardingMessage,
  showForwardModal,
  showStartChatModal,
  isCreatingChat,
  forwardableConversations,
  handleCreateChat,
  handleForwardConfirm,
  handleForwardSearch,
  handleForwardLoadMore,
} = useWhatsAppActions(activeInteractionTab, loadConversationsForChannel, toast);

const {
  selectedNumber,
  selectedNumberObj,
  selectedContactNumber,
  showMicrophoneDropdown,
  showDialerDropdown,
  showIVRDropdown,
  searchTerm,
  showSearchDropdown,
  allContactNumbers,
  validContactNumbers,
  contactPhoneNumbers,
  getRecipientPhoneNumber,
  hasValidPhoneNumber,
  onPhoneNumberChange,
  formatPhoneNumberWithCountry,
  toggleDeleteContactPhoneNumber,
  deleteContactPhoneNumber,
  getPhoneNumbers,
  selectMicrophone,
  handleClickOutside,
  handleDialerClickOutside,
  toggleDialerDropdown,
  selectPhoneNumberToEdit,
  handlePhoneDataChange,
  showPhoneNumberMenu,
  getContacts,
  debouncedSearch,
  debouncedSearchFunc,
  selectContact,
  copyToClipboard,
} = usePhoneSelection(reactiveContact, phoneNumbers, deletePhoneMutation, store, account, toast);

const { setupSocketListeners, cleanupSocketListeners } = useSocketListeners(
  parentCallSid,
  currentCallSid,
  lastCallSid,
  to,
  loadChannels
);

const {
  editBucketObj,
  selectedFollowUpOption,
  selectedMeeting,
  nextTaskKey,
  showCloseConfirmationModal,
  taskTypeOptions,
  bucketOptionsFormatted,
  followUpOnOptions,
  setValues,
  closeModal,
  editBucket,
  handleSaveAndLogCall,
  handleNextTaskSave,
  handleCancel,
  handlePromptSelectDisposition,
  openReminderFollowupModal,
  closeParentModal,
  closeReminderFollowupModal,
} = useBucketForm(
  props,
  emit,
  updateBucket,
  registerBucket,
  registerActivityLog,
  logCallBySid,
  isCallDispositionRequired,
  selectedCallDisposition,
  callDispositionError,
  mustLogAfterCall,
  currentCallSid,
  hasCallBeenLogged,
  isPostCallLocked,
  account,
  reactiveContact,
  bucketOptions,
  toast,
  router
);

// Cross-cutting UI state (stays in component)
const showModal = ref(true);

const { matches: is3Up } = useResponsiveBreakpoint(WORKSPACE_3UP_MIN_PX);
const {
  value: isTemplatesOpen,
  toggle: toggleTemplatesModal,
  close: closeTemplatesModal,
} = usePersistentToggle("sb_workspace_templates_drawer", false);

function isEditableFocus(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  return false;
}

function handleWorkspaceKeydown(e: KeyboardEvent) {
  if (!showModal.value) return;

  if (e.key === "Escape") {
    if (isPostCallLocked.value) return;
    if (!is3Up.value && isTemplatesOpen.value) {
      e.preventDefault();
      closeTemplatesModal();
      return;
    }
    closeModal();
    return;
  }

  if (e.key === "]") {
    if (is3Up.value) return;
    if (isEditableFocus()) return;
    e.preventDefault();
    toggleTemplatesModal();
  }
}

const showAiPersonalizationModal = ref(false);
const aiPersonalizationContext = ref({
  defaultType: "",
  contactName: "",
  icpName: "",
  contactId: "",
  icpId: "",
});

const showNotesModal = ref(false);
const showLastInteractionModal = ref(false);
const showBookMeetingModal = ref(false);
const showReminderFollowupModal = ref(false);
const showEditContactModal = ref(false);
const showDeleteConfirmationModal = ref(false);
const pendingCueCardContent = ref<any>(null);

// Set when an Interaction History entry is clicked, to auto-open that
// specific conversation in the matching channel panel. `undefined` means
// "no history click yet" (fall back to the initial prop); `null` is a
// deliberate "this activity log had no captured conversation id" — it must
// NOT fall back, or clicking such an entry would reopen a stale conversation.
const historyEmailThreadId = ref<string | null | undefined>(undefined);
const historyLinkedInChatId = ref<string | null | undefined>(undefined);
const historyWhatsAppChatId = ref<string | null | undefined>(undefined);

// The specific message to scroll to/highlight once the conversation above opens.
const historyLinkedInMessageId = ref<string | null>(null);
const historyWhatsAppMessageId = ref<string | null>(null);

// Computed properties for cross-cutting concerns
const contactFullName = computed(() =>
  `${reactiveContact.value?.fname || ""} ${reactiveContact.value?.lname || ""}`.trim()
);

const effectiveEmailThreadId = computed(() =>
  historyEmailThreadId.value !== undefined
    ? historyEmailThreadId.value
    : (props.initialEmailThreadId ?? null)
);
const effectiveLinkedInChatId = computed(() =>
  historyLinkedInChatId.value !== undefined
    ? historyLinkedInChatId.value
    : (props.initialChatId ?? null)
);
const effectiveWhatsAppChatId = computed(() =>
  historyWhatsAppChatId.value !== undefined
    ? historyWhatsAppChatId.value
    : (props.initialChatId ?? null)
);

// Cross-cutting handlers
function handleSwitchChannelWithContent(payload: any) {
  const channelToTab: any = {
    call: "calls",
    email: "email",
    linkedin_connection: "linkedin",
    linkedin_followup: "linkedin",
    whatsapp: "whatsapp",
    sms: "sms",
  };
  const targetTab = channelToTab[payload.channel] || "calls";
  pendingCueCardContent.value = payload;
  activeInteractionTab.value = targetTab;
}

function handleOpenAiPersonalization(payload: any) {
  aiPersonalizationContext.value = {
    defaultType: payload.defaultType || "",
    contactName: payload.contactName || "",
    icpName: payload.icpName || "",
    contactId: payload.contactId || "",
    icpId: payload.icpId || "",
  };
  showAiPersonalizationModal.value = true;
}

function handleDeclineIncomingCall() {
  emit("closeTaskFollowupModal");
}

function handleNavigateFromInteractionHistory(
  tab: string,
  conversationId: string | null,
  messageId: string | null
) {
  showLastInteractionModal.value = false;
  if (tab === "email") {
    historyEmailThreadId.value = conversationId;
  } else if (tab === "linkedin") {
    historyLinkedInChatId.value = conversationId;
    historyLinkedInMessageId.value = messageId;
  } else if (tab === "whatsapp") {
    historyWhatsAppChatId.value = conversationId;
    historyWhatsAppMessageId.value = messageId;
  }
  activeInteractionTab.value = tab;
}

function getContact() {
  emit("refreshBuckets");
}

// Webhook composables
useMessageWebhooks({
  onLinkedInMessage: (payload) => {
    loadConversationsForChannel("linkedin");
    if (reactiveContact.value?.id && payload?.contactId === reactiveContact.value.id) {
      incrementBadge("linkedin");
    }
  },
  onWhatsAppMessage: (payload) => {
    loadConversationsForChannel("whatsapp");
    if (reactiveContact.value?.id && payload?.contactId === reactiveContact.value.id) {
      incrementBadge("whatsapp");
    }
  },
});

useEmailWebhooks({
  accountId: computed(() => emailChannel.value?.accountId) as any,
  isEmailChannelSelected: computed(() => activeInteractionTab.value === "email") as any,
  onEmailReceived: (payload: any, helpers: any) => {
    try {
      if (!helpers.belongsToAccount(payload)) {
        return;
      }
      if (activeInteractionTab.value === "email") {
        loadConversationsForChannel("email");
      }
      if (reactiveContact.value?.id && payload?.contactId === reactiveContact.value.id) {
        incrementBadge("email");
      }
    } catch (error) {
      console.error("Error handling email received event:", error);
    }
  },
  onEmailOpened: () => {},
  onEmailLinkClicked: () => {},
  showNotifications: false,
});

// Lifecycle hooks
onMounted(async () => {
  setValues();
  await loadChannels();
  setupSocketListeners();
  document.addEventListener("keydown", handleWorkspaceKeydown);
  _nowInterval = setInterval(() => {
    nowRef.value = new Date();
  }, 60_000);
});

onUnmounted(() => {
  disableBeforeUnload();
  cleanupSocketListeners();
  document.removeEventListener("mousedown", handleClickOutside);
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleWorkspaceKeydown);
  if (_nowInterval) clearInterval(_nowInterval);
});
</script>

<style scoped>
::v-deep input[type="date"]::-webkit-calendar-picker-indicator {
  cursor: pointer;
}

/* Workspace layout */
.workspace-layout {
  display: flex;
  position: relative;
  height: calc(92vh - 16px);
  overflow: hidden;
  gap: 16px;
  padding: 16px;
}

.workspace-panel {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(0, 0, 0, 0.05);
}

.workspace-panel--left {
  width: 360px;
  flex-shrink: 0;
}

.workspace-panel--center {
  flex: 0.9;
  min-width: 0;
}

.workspace-panel--right {
  flex: 1.1;
  min-width: 0;
}

.templates-fab {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 10061;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px 0 12px;
  height: 34px;
  border: none;
  background: #2d3bff;
  color: #fff;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  box-shadow:
    0 2px 8px rgba(45, 59, 255, 0.3),
    0 8px 20px rgba(45, 59, 255, 0.18);
  transition:
    background 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.15s ease,
    color 0.2s ease;
}

.templates-fab:hover {
  background: #253ce5;
  box-shadow:
    0 3px 12px rgba(45, 59, 255, 0.38),
    0 10px 26px rgba(45, 59, 255, 0.24);
  transform: translateY(-1px);
}

.templates-fab:active {
  transform: translateY(0);
}

.templates-fab.active {
  background: #fff;
  color: #2d3bff;
  box-shadow:
    0 0 0 1px rgba(45, 59, 255, 0.2),
    0 4px 14px rgba(45, 59, 255, 0.2);
}

.interaction-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.upcoming-meeting-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  background: #f0fdf4;
  border-bottom: 1px solid #86efac;
  flex-shrink: 0;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.upcoming-meeting-banner:hover {
  background: #dcfce7;
}

.upcoming-meeting-banner:focus-visible {
  outline: 2px solid #16a34a;
  outline-offset: -2px;
}

.upcoming-meeting-wrapper {
  flex-shrink: 0;
}

.upcoming-meeting-banner__chevron {
  color: #16a34a;
  opacity: 0.7;
  flex-shrink: 0;
  margin-left: auto;
  transition: transform 0.15s ease;
}

.upcoming-meeting-banner__chevron--expanded {
  transform: rotate(90deg);
}

.upcoming-meetings-list {
  display: flex;
  flex-direction: column;
  background: #f7fefa;
  border-bottom: 1px solid #86efac;
  max-height: 180px;
  overflow-y: auto;
}

.upcoming-meetings-list__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 16px 8px 20px;
  border: none;
  border-top: 1px solid #dcfce7;
  background: transparent;
  text-align: left;
  transition: background-color 0.15s ease;
}

.upcoming-meetings-list__item:hover {
  background: #dcfce7;
}

.upcoming-meetings-list__item-icon {
  font-size: 14px;
  color: #16a34a;
  flex-shrink: 0;
}

.upcoming-meetings-list__item-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
}

.upcoming-meetings-list__item-title {
  font-size: 11px;
  font-weight: 600;
  color: #14532d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upcoming-meetings-list__item-date {
  font-size: 10.5px;
  color: #166534;
}

.upcoming-meetings-list__item-action {
  flex-shrink: 0;
  border: 1px solid #86efac;
  background: #ffffff;
  color: #15803d;
  font-size: 10.5px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}

.upcoming-meetings-list__item-action:hover {
  background: #16a34a;
  color: #ffffff;
}

.upcoming-meeting-banner__icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: #dcfce7;
  border-radius: 6px;
  color: #16a34a;
  flex-shrink: 0;
}

.upcoming-meeting-banner__body {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
}

.upcoming-meeting-banner__title {
  font-size: 12px;
  font-weight: 600;
  color: #15803d;
  white-space: nowrap;
}

.upcoming-meeting-banner__detail {
  font-size: 11px;
  color: #166534;
  display: flex;
  align-items: center;
  gap: 6px;
}

.upcoming-meeting-banner__more {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #bbf7d0;
  color: #15803d;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
  white-space: nowrap;
  cursor: help;
}

/* Same layout as .upcoming-meeting-banner above, but colors come entirely from
   inline styles (the opportunity's own stage colors) rather than fixed values. */
.opportunity-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  border-bottom: 1px solid transparent;
  flex-shrink: 0;
}

.opportunity-banner__icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  flex-shrink: 0;
}

.opportunity-banner__body {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
  flex: 1;
}

.opportunity-banner__title {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.opportunity-banner__stage-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid;
  white-space: nowrap;
}

.opportunity-banner__detail {
  font-size: 11px;
  opacity: 0.85;
}

.opportunity-banner__view {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border: none;
  background: none;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  padding: 2px 4px;
}

.interaction-panels-container {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.microphone-dropdown {
  position: absolute;
  top: 45px;
  left: 0;
  min-width: 160px;
  background: #fff;
  border: 1px solid #e6e8ec;
  border-radius: 8px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 1001;
  max-height: 200px;
  overflow-y: auto;
}
.microphone-dropdown ul {
  list-style: none;
  margin: 0;
  padding: 0;
}
.microphone-dropdown li {
  font-size: 0.7rem;
}

.microphone-dropdown li:hover {
  font-size: 0.7rem;
  cursor: pointer;
  background: #f8f7ff;
}

.call-panel-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
}

.call-panel-footer {
  border-top: 1px solid #e4e4e7;
  padding: 12px 15px;
  margin-top: auto;
  background-color: #ffffff;
  position: sticky;
  bottom: 0;
  z-index: 10;
}

.footer-error-message {
  margin-top: 8px;
  font-size: 12px;
  color: #dc3545;
  text-align: center;
}

.phone-numbers-header {
  margin-bottom: 5px;
  align-items: center;
}

.phone-numbers-title {
  font-size: 12px;
  color: #646464;
  font-weight: 400;
  margin: 0;
  line-height: 1.5;
  display: flex;
  align-items: center;
}

.phone-numbers-actions {
  min-width: 0;
}

.ai-generate-wrapper {
  position: relative;
  width: 240px;
  flex-shrink: 1;
  min-width: 200px;
  max-width: 240px;
}

.ai-generate-wrapper-full {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
}

.ai-generate-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #3e3efe;
  z-index: 10;
  pointer-events: none;
}

.ai-generate-select {
  width: 100%;
}

.ai-generate-select :deep(.ui-select__field) {
  padding-left: 36px !important;
}

.has-error ::v-deep .select-header {
  border-color: #dc3545 !important;
}

.has-error ::v-deep .select-header:hover {
  border-color: #dc3545 !important;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
}

@media (max-width: 1199.98px) {
  .workspace-layout {
    max-width: 1100px;
    margin: 0 auto;
    gap: 20px;
    padding: 16px;
  }

  .workspace-panel--left {
    flex: 1;
  }

  .workspace-panel--center {
    flex: 1;
  }
}

.post-call-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10055;
}

.post-call-loading-card {
  background: #fff;
  border-radius: 10px;
  padding: 32px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
}

.post-call-loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: post-call-spin 0.8s linear infinite;
}

@keyframes post-call-spin {
  to {
    transform: rotate(360deg);
  }
}

.post-call-loading-text {
  margin: 0;
  font-size: 0.9rem;
  color: #4b5563;
  font-weight: 500;
}
</style>
